/**
 * Adventure Mode Storage
 * アドベンチャーモードの永続化
 *
 * Created: 2025-12-17
 */

import type { AdventureProgress, NpcBattleRecord, NpcId } from './types';
import { ADVENTURE_STORAGE_KEY, ADVENTURE_DATA_VERSION } from './types';

// ============================================================
// 初期データ
// ============================================================

/**
 * 初期の対戦記録
 */
export function createInitialBattleRecord(): NpcBattleRecord {
  return {
    wins: 0,
    losses: 0,
    firstClearDate: null,
    lastBattleDate: null,
    todayBattleCount: 0,
    todayRewardCount: 0,
  };
}

/**
 * 初期の進行データ
 */
export function createInitialProgress(): AdventureProgress {
  return {
    clearedNpcs: [],
    npcRecords: {},
    totalBattles: 0,
    totalWins: 0,
    totalLosses: 0,
    titles: [],
    version: ADVENTURE_DATA_VERSION,
    lastUpdated: new Date().toISOString(),
  };
}

// ============================================================
// 読み込み・保存
// ============================================================

/**
 * 進行データを読み込む
 */
export function loadProgress(): AdventureProgress {
  try {
    const stored = localStorage.getItem(ADVENTURE_STORAGE_KEY);
    if (!stored) {
      return createInitialProgress();
    }

    const parsed = JSON.parse(stored) as AdventureProgress;

    // バージョンチェックとマイグレーション
    if (!parsed.version || parsed.version < ADVENTURE_DATA_VERSION) {
      return migrateProgress(parsed);
    }

    // デイリーリセットチェック
    return checkDailyReset(parsed);
  } catch (error) {
    console.error('Failed to load adventure progress:', error);
    return createInitialProgress();
  }
}

/**
 * 進行データを保存
 */
export function saveProgress(progress: AdventureProgress): void {
  try {
    const toSave: AdventureProgress = {
      ...progress,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(ADVENTURE_STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Failed to save adventure progress:', error);
  }
}

/**
 * 進行データをリセット
 */
export function resetProgress(): AdventureProgress {
  const initial = createInitialProgress();
  saveProgress(initial);
  return initial;
}

// ============================================================
// マイグレーション
// ============================================================

/**
 * 古いバージョンのデータをマイグレーション
 */
function migrateProgress(oldProgress: Partial<AdventureProgress>): AdventureProgress {
  // 現状はv1のみなので、デフォルト値で埋める
  const migrated: AdventureProgress = {
    clearedNpcs: oldProgress.clearedNpcs || [],
    npcRecords: oldProgress.npcRecords || {},
    totalBattles: oldProgress.totalBattles || 0,
    totalWins: oldProgress.totalWins || 0,
    totalLosses: oldProgress.totalLosses || 0,
    titles: oldProgress.titles || [],
    version: ADVENTURE_DATA_VERSION,
    lastUpdated: new Date().toISOString(),
  };

  saveProgress(migrated);
  return migrated;
}

// ============================================================
// デイリーリセット
// ============================================================

/**
 * 日付が変わっていたらデイリーカウントをリセット
 */
function checkDailyReset(progress: AdventureProgress): AdventureProgress {
  const today = new Date().toISOString().split('T')[0];
  const lastUpdate = progress.lastUpdated?.split('T')[0];

  // 同じ日ならリセット不要
  if (today === lastUpdate) {
    return progress;
  }

  // 日付が変わったのでデイリーカウントをリセット
  const resetRecords: typeof progress.npcRecords = {};

  for (const [npcId, record] of Object.entries(progress.npcRecords)) {
    if (record) {
      resetRecords[npcId as NpcId] = {
        ...record,
        todayBattleCount: 0,
        todayRewardCount: 0,
      };
    }
  }

  const updated: AdventureProgress = {
    ...progress,
    npcRecords: resetRecords,
    lastUpdated: new Date().toISOString(),
  };

  saveProgress(updated);
  return updated;
}

// ============================================================
// 更新ヘルパー
// ============================================================

/**
 * NPC記録を更新（存在しない場合は作成）
 */
export function updateNpcRecord(
  progress: AdventureProgress,
  npcId: NpcId,
  updater: (record: NpcBattleRecord) => NpcBattleRecord
): AdventureProgress {
  const currentRecord = progress.npcRecords[npcId] || createInitialBattleRecord();
  const updatedRecord = updater(currentRecord);

  return {
    ...progress,
    npcRecords: {
      ...progress.npcRecords,
      [npcId]: updatedRecord,
    },
  };
}

/**
 * クリア済みNPCを追加
 */
export function addClearedNpc(
  progress: AdventureProgress,
  npcId: NpcId
): AdventureProgress {
  if (progress.clearedNpcs.includes(npcId)) {
    return progress;
  }

  return {
    ...progress,
    clearedNpcs: [...progress.clearedNpcs, npcId],
  };
}

/**
 * 称号を追加
 */
export function addTitle(
  progress: AdventureProgress,
  title: string
): AdventureProgress {
  if (progress.titles.includes(title)) {
    return progress;
  }

  return {
    ...progress,
    titles: [...progress.titles, title],
  };
}

/**
 * 統計を更新
 */
export function updateStats(
  progress: AdventureProgress,
  result: 'win' | 'lose'
): AdventureProgress {
  return {
    ...progress,
    totalBattles: progress.totalBattles + 1,
    totalWins: result === 'win' ? progress.totalWins + 1 : progress.totalWins,
    totalLosses: result === 'lose' ? progress.totalLosses + 1 : progress.totalLosses,
  };
}
